import React, { useEffect, useRef, useState } from "react";
import {
  ArrowUpRight,
  ExternalLink,
  DollarSign,
  Eye,
  Camera,
  Bot,
  Megaphone,
  Layers,
  FileText,
  Target,
  Volume2,
  VolumeX,
  Download,
  ShieldCheck,
  Quote,
} from "lucide-react";
import RevealRoot from "@/components/landing/RevealRoot";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";
import { usePrice } from "@/lib/price";

const WHOP = "https://whop.com/checkout/plan_hfsvR7lsOjQds";
const AFFILIATE_SIGNUP = "https://whop.com/jobhacki/affiliates";
const PLAYBOOK_PDF = "/affiliate/playbook.pdf";
const CONTENT_REWARDS =
  "https://contentrewards.com/discover/755ae80e-2804-4857-ae22-5d07f1190aa2";

const REELS = {
  painHook: "https://storage.googleapis.com/jobhacki-assets/affiliate/reels/painhook.mp4",
  trendJack: "https://storage.googleapis.com/jobhacki-assets/affiliate/reels/trendjack.mp4",
  contrarian: "https://storage.googleapis.com/jobhacki-assets/affiliate/reels/contrarian.mp4",
  aiPain: "https://storage.googleapis.com/jobhacki-assets/affiliate/creators/pain_hook.mp4",
};

const AI_STILLS = {
  c2: "https://storage.googleapis.com/jobhacki-assets/affiliate/creators/creator_2.png",
  c3: "https://storage.googleapis.com/jobhacki-assets/affiliate/creators/creator_3.png",
};

/* ───────── Hero ───────── */
function Hero() {
  const { price } = usePrice();
  return (
    <section
      data-testid="affiliate-hero"
      className="relative overflow-hidden border-b-4 border-[#0a0a0a] bg-[#0a0a0a] text-[#FDFBF7]"
    >
      <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
      <div className="pointer-events-none absolute -top-20 -right-16 w-[320px] h-[320px] rounded-full bg-[#FFE600] border-4 border-[#FFE600] hidden md:block opacity-90" />
      <div className="pointer-events-none absolute -bottom-24 -left-12 w-[240px] h-[240px] bg-[#FF3B30] border-4 border-[#FF3B30] rotate-12 hidden md:block" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-16 md:pb-20">
        <div className="reveal max-w-4xl">
          <div className="inline-flex items-center gap-2 bg-[#FFE600] text-[#0a0a0a] border-2 border-[#FFE600] px-3 py-1.5 font-accent text-xs md:text-sm shadow-brutal-sm">
            <DollarSign size={14} strokeWidth={3} /> JOBHACKI AFFILIATE PROGRAM
          </div>
          <h1
            data-testid="affiliate-hero-title"
            aria-label="Join our Affiliate Program. Hosted by Whop."
            className="font-display mt-7 text-[44px] sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.9]"
          >
            Join our{" "}
            <span className="bg-[#FFE600] text-[#0a0a0a] px-2">Affiliate Program</span>.
            <br />
            Hosted by{" "}
            <span className="bg-[#0055FF] text-white px-2">Whop</span>.
          </h1>
          <p className="mt-6 max-w-2xl font-mono-body text-base md:text-lg text-[#FDFBF7]/80 leading-relaxed">
            JobHacki is the easiest sale in the AI / money / career niche — a one-time
            {price} membership packed with hundreds of income &amp; job playbooks and a full
            toolbox. Cheap enough to convert cold traffic, broad enough for any
            audience. You promote, Whop tracks every sale and pays you automatically.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3.5">
            <a
              href={AFFILIATE_SIGNUP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="affiliate-hero-cta-primary"
              className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FFE600] text-[#0a0a0a] border-4 border-[#FFE600] px-6 py-4 font-accent text-base md:text-lg shadow-brutal"
            >
              Become an affiliate <ExternalLink size={16} strokeWidth={3} />
            </a>
            <a
              href={PLAYBOOK_PDF}
              download
              data-testid="affiliate-hero-cta-secondary"
              className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FF3B30] text-white border-4 border-[#FF3B30] px-6 py-4 font-accent text-base md:text-lg shadow-brutal"
            >
              <Download size={18} strokeWidth={3} /> Download the free Affiliate Playbook
            </a>
          </div>
          <p className="mt-4 font-mono-body text-xs md:text-sm text-[#FDFBF7]/55">
            Free to join · instant tracking · no follower minimum
          </p>
        </div>

        {/* Trust row */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {[
            { n: "$1.6B", l: "Whop's valuation (Tether-backed, 2026)" },
            { n: "$2.67B+", l: "paid through Whop to creators" },
            { n: "Iman Gadzhi", l: "co-owner & investor of Whop" },
            { n: "1,700+", l: "JobHacki members & counting" },
          ].map((t, i) => (
            <div
              key={t.l}
              data-testid={`trust-${i}`}
              className="bg-[#FDFBF7] text-[#0a0a0a] border-4 border-[#FFE600] shadow-[6px_6px_0_0_#FFE600] p-4 md:p-5 reveal"
            >
              <div className="font-display text-2xl md:text-3xl leading-tight">
                {t.n}
              </div>
              <div className="font-mono-body text-[11px] md:text-xs mt-2 text-[#0a0a0a]/65 leading-snug">
                {t.l}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-[#FFE600] text-[#0a0a0a] border-4 border-[#0a0a0a] shadow-brutal p-5 md:p-6 reveal flex gap-4 items-start">
          <ShieldCheck size={28} strokeWidth={2.5} className="shrink-0 mt-1" />
          <p className="font-mono-body text-sm md:text-base">
            <b>Total transparency.</b> Your link is a Whop affiliate link — every sale
            is tracked and paid by Whop, not us. We literally <b>can&apos;t hide a sale</b>{" "}
            even if we wanted to. You see your numbers, Whop pays you.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ───────── Why converts ───────── */
function WhyConverts() {
  const { price } = usePrice();
  const cards = [
    {
      tag: "LOW FRICTION",
      title: `${price}, one-time`,
      body: "No subscription, nothing to cancel. It's an impulse-priced no-brainer — the hardest part of selling (price objection) is basically gone.",
      bg: "bg-[#FFE600] text-[#0a0a0a]",
    },
    {
      tag: "BROAD APPEAL",
      title: "Everyone's audience",
      body: "Job seekers, side-hustlers, AI-curious, recent grads, \"make money online.\" If your audience breathes the internet, this fits.",
      bg: "bg-[#0055FF] text-white",
    },
    {
      tag: "REAL VALUE",
      title: "Hundreds of playbooks + tools",
      body: "It actually delivers — so refunds are low and your audience thanks you. You're recommending something good.",
      bg: "bg-[#FF3B30] text-white",
    },
  ];
  return (
    <section
      data-testid="affiliate-why"
      className="border-b-4 border-[#0a0a0a] bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="mb-12 reveal max-w-3xl">
          <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-4">
            WHY IT CONVERTS
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95]">
            Why JobHacki{" "}
            <span className="bg-[#FFE600] px-2">converts.</span>
          </h2>
          <p className="font-mono-body mt-5 text-[#0a0a0a]/75">
            The math that makes this an easy yes for your audience — and steady commissions for you.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {cards.map((c, i) => (
            <div
              key={c.title}
              data-testid={`affiliate-why-${i}`}
              className={`${c.bg} border-4 border-[#0a0a0a] shadow-brutal hover-pop p-7 reveal`}
            >
              <div className="font-accent text-xs bg-[#0a0a0a] text-[#FFE600] inline-block px-2.5 py-1 mb-4">
                {c.tag}
              </div>
              <h3 className="font-display text-3xl md:text-4xl leading-tight">{c.title}</h3>
              <p className="font-mono-body text-sm md:text-base mt-3 opacity-90 leading-relaxed">
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────── Two ways to get paid ───────── */
function TwoWays() {
  const { price } = usePrice();
  return (
    <section
      data-testid="affiliate-two-ways"
      className="border-b-4 border-[#0a0a0a] bg-[#0a0a0a] text-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="mb-12 reveal max-w-3xl">
          <div className="font-accent text-sm bg-[#FFE600] text-[#0a0a0a] inline-block px-3 py-1 mb-4">
            TWO WAYS TO GET PAID
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95]">
            <span className="bg-[#FFE600] text-[#0a0a0a] px-2">Two</span> ways to get paid.
          </h2>
          <p className="font-mono-body mt-5 text-[#FDFBF7]/75">
            You don&apos;t even need a sale to start earning.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <article
            data-testid="affiliate-way-sale"
            className="bg-[#FDFBF7] text-[#0a0a0a] border-4 border-[#FDFBF7] shadow-brutal-lg p-7 md:p-8 reveal"
          >
            <div className="w-14 h-14 bg-[#FFE600] text-[#0a0a0a] border-2 border-[#0a0a0a] flex items-center justify-center mb-5">
              <DollarSign size={26} strokeWidth={2.5} />
            </div>
            <span className="inline-block bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] px-2.5 py-1 font-accent text-xs mb-3">
              PER SALE
            </span>
            <h3 className="font-display text-3xl md:text-4xl">Affiliate commissions</h3>
            <p className="font-mono-body text-sm md:text-base mt-4 text-[#0a0a0a]/85 leading-relaxed">
              Earn a commission on every {price} sale through your link — tracked + paid automatically by Whop. Promote the whole bundle or any single product.
            </p>
            <a
              href={AFFILIATE_SIGNUP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="way-sale-cta"
              className="hover-pop mt-6 inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white border-4 border-[#0a0a0a] px-5 py-3.5 font-accent text-sm md:text-base shadow-brutal"
            >
              Become an affiliate <ExternalLink size={14} strokeWidth={3} />
            </a>
          </article>

          <article
            data-testid="affiliate-way-views"
            className="bg-[#FF00FF] text-white border-4 border-[#FFE600] shadow-[8px_8px_0_0_#FFE600] p-7 md:p-8 reveal"
          >
            <div className="w-14 h-14 bg-[#FFE600] text-[#0a0a0a] border-2 border-[#0a0a0a] flex items-center justify-center mb-5">
              <Eye size={26} strokeWidth={2.5} />
            </div>
            <span className="inline-block bg-[#FFE600] text-[#0a0a0a] border-2 border-[#0a0a0a] px-2.5 py-1 font-accent text-xs mb-3">
              PER VIEW
            </span>
            <h3 className="font-display text-3xl md:text-4xl">Content rewards</h3>
            <p className="font-mono-body text-sm md:text-base mt-4 text-white/90 leading-relaxed">
              Get <b>paid per view</b> for posting JobHacki clips through our{" "}
              <a
                href={CONTENT_REWARDS}
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold underline underline-offset-4 decoration-2 text-[#FFE600]"
              >
                Content Rewards campaign
              </a>{" "}
              on Whop — you earn from the views themselves, even before anyone buys. Stack it on top of commissions.
            </p>
            <a
              href={CONTENT_REWARDS}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="way-views-cta"
              className="hover-pop mt-6 inline-flex items-center justify-center gap-2 bg-[#FFE600] text-[#0a0a0a] border-4 border-[#0a0a0a] px-5 py-3.5 font-accent text-sm md:text-base shadow-brutal"
            >
              Open Content Rewards <ExternalLink size={14} strokeWidth={3} />
            </a>
          </article>
        </div>
      </div>
    </section>
  );
}

/* ───────── Framework grid ───────── */
function Framework() {
  const { price } = usePrice();
  const lanes = [
    {
      tag: "ORGANIC",
      title: "Influencer marketing",
      body: "Show JobHacki inside your normal content — a \"here's how I apply to jobs in 2026\" routine, a tool walkthrough, a results post. Native beats scripted.",
      Icon: Megaphone,
      bg: "bg-[#FFE600] text-[#0a0a0a]",
    },
    {
      tag: "FACELESS",
      title: "AI influencer marketing",
      body: "No face? Run a faceless page (Threads / X / Shorts) on AI-income & career angles, post daily, drop your link. We give you the hooks and the cadence.",
      Icon: Bot,
      bg: "bg-[#0055FF] text-white",
    },
    {
      tag: "UGC",
      title: "UGC & short-form",
      body: "Talking-head, screen-rec, before/after. Use our hook science (0.6s open, pain / contrarian / pleasure) to stop the scroll.",
      Icon: Camera,
      bg: "bg-[#FF3B30] text-white",
    },
    {
      tag: "BUILT-IN",
      title: "Our content frameworks",
      body: "Steal our video, thumbnail & caption systems — the same ones JobHacki runs. Plug-and-play hooks, formats, and edit cadence.",
      Icon: Layers,
      bg: "bg-[#00FF66] text-[#0a0a0a]",
    },
    {
      tag: "SEO",
      title: "Blogs & articles",
      body: `"Best ${price} AI tools," "how to beat the ATS," "AI side hustles 2026" — write once, rank, and earn on autopilot with your link in the post.`,
      Icon: FileText,
      bg: "bg-[#FF00FF] text-white",
    },
    {
      tag: "PAID",
      title: "Run ads (if you want)",
      body: `Advanced affiliates can run Meta / TikTok ads to a ${price} offer. We share the angles + creative matrix; you keep the spread.`,
      Icon: Target,
      bg: "bg-white text-[#0a0a0a]",
    },
  ];
  return (
    <section
      data-testid="affiliate-framework"
      className="border-b-4 border-[#0a0a0a] bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="mb-12 reveal max-w-3xl">
          <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-4">
            EVERY WAY TO WIN
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95]">
            Every way to{" "}
            <span className="bg-[#FFE600] px-2">win</span> promoting JobHacki.
          </h2>
          <p className="font-mono-body mt-5 text-[#0a0a0a]/75">
            Pick the lane that fits you. The full <b>Affiliate Marketing Framework</b> breaks each one down step by step.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {lanes.map((l, i) => {
            const Icon = l.Icon;
            return (
              <article
                key={l.title}
                data-testid={`framework-lane-${i}`}
                className={`${l.bg} border-4 border-[#0a0a0a] shadow-brutal hover-pop p-6 md:p-7 reveal`}
              >
                <div className="w-12 h-12 bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] flex items-center justify-center mb-5">
                  <Icon size={22} strokeWidth={2.5} />
                </div>
                <span className="inline-block font-accent text-[10px] md:text-xs bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] px-2 py-0.5 mb-3">
                  {l.tag}
                </span>
                <h3 className="font-display text-xl md:text-2xl leading-tight">
                  {l.title}
                </h3>
                <p className="font-mono-body text-sm mt-3 leading-relaxed opacity-90">
                  {l.body}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ───────── Promote whole stack ───────── */
function StackOrProduct() {
  const { price } = usePrice();
  const products = [
    { label: "Playbook Directory & ebooks", href: "/playbooks", caption: "Hundreds of income, job & AI playbooks + ebooks across ~100 categories. The broadest, easiest pitch." },
    { label: "Tool Directory", href: "/toolbox", caption: "Hundreds of AI tools, prompts & thousands of scripts, GitHub repos, and AI business ideas / models / workflows." },
    { label: "Resume Builder", href: "/resume", caption: "A role-targeted, print-ready resume in minutes. Huge for the job-seeker audience." },
    { label: "Job Directory", href: "/jobs", caption: "Thousands of live job listings, matched to a resume & skills. Pure utility hook." },
    { label: "Readiness Simulator", href: "/simulator", caption: "Scores & sharpens interview answers. Great \"try this\" interactive angle." },
    { label: "OmniCut", href: "/omnicut", caption: "Frame-by-frame viral edit blueprints for creators — perfect for a content-creator audience." },
  ];
  return (
    <section
      data-testid="affiliate-stack"
      className="border-b-4 border-[#0a0a0a] bg-[#FFE600]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="mb-12 reveal max-w-3xl">
          <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-4">
            ONE BUNDLE · MANY ANGLES
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95]">
            Promote the{" "}
            <span className="bg-[#0a0a0a] text-[#FFE600] px-2">whole stack</span> — or any single product.
          </h2>
          <p className="font-mono-body mt-5 text-[#0a0a0a]/80">
            Pitch the {price} All-Access bundle for the easiest sale, or zero in on the one tool your audience wants. Each links to its own page you can send traffic to.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {products.map((p, i) => (
            <a
              key={p.label}
              href={p.href}
              data-testid={`stack-product-${i}`}
              className="hover-pop bg-white border-4 border-[#0a0a0a] shadow-brutal p-5 md:p-6 reveal flex flex-col"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-xl md:text-2xl leading-tight">
                  {p.label}
                </h3>
                <ArrowUpRight size={18} strokeWidth={3} className="shrink-0 mt-1" />
              </div>
              <p className="font-mono-body text-sm mt-2 text-[#0a0a0a]/80 leading-relaxed">
                {p.caption}
              </p>
            </a>
          ))}
        </div>
        <p className="font-mono-body text-sm md:text-base mt-7 text-[#0a0a0a]/80">
          Either way it&apos;s the same <b>{price} one-time</b> unlock — so every product is the same easy price point to sell.
        </p>
      </div>
    </section>
  );
}

/* ───────── Reel card ───────── */
function Reel({ src, img, tag, chip, cap, testid, accent = "#FFE600" }) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  return (
    <figure
      data-testid={testid}
      className="relative bg-[#0a0a0a] border-4 border-[#FFE600] shadow-[6px_6px_0_0_#FFE600] overflow-hidden reveal"
    >
      <div className="relative aspect-[9/14] bg-[#0a0a0a]">
        {src ? (
          <video
            ref={ref}
            src={src}
            muted={muted}
            autoPlay
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <img
            src={img}
            alt={cap}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <span
          className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[#FFE600] text-[#0a0a0a] border-2 border-[#0a0a0a] px-2.5 py-1 font-accent text-[10px] shadow-brutal-sm"
          style={{ background: accent }}
        >
          <span className="live-dot" /> {tag}
        </span>
        {chip && (
          <span className="absolute bottom-3 left-3 right-3 bg-[#0a0a0a] text-[#FFE600] border-2 border-[#FFE600] px-2.5 py-1.5 font-accent text-[11px] text-center">
            {chip}
          </span>
        )}
        {src && (
          <button
            onClick={() => {
              setMuted((m) => !m);
              if (ref.current) ref.current.muted = !muted ? true : false;
            }}
            className="absolute top-3 right-3 hover-pop bg-[#FFE600] text-[#0a0a0a] border-2 border-[#0a0a0a] w-8 h-8 inline-flex items-center justify-center shadow-brutal-sm"
            aria-label="Toggle sound"
          >
            {muted ? <VolumeX size={12} strokeWidth={3} /> : <Volume2 size={12} strokeWidth={3} />}
          </button>
        )}
      </div>
      <figcaption className="bg-[#0a0a0a] text-[#FDFBF7] font-mono-body text-xs px-3 py-2 text-center border-t-2 border-[#FFE600]">
        {cap}
      </figcaption>
    </figure>
  );
}

/* ───────── Showcase reels ───────── */
function Showcase() {
  return (
    <section
      data-testid="affiliate-showcase"
      className="border-b-4 border-[#0a0a0a] bg-[#0a0a0a] text-[#FDFBF7] relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-15 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="mb-12 reveal max-w-3xl">
          <div className="font-accent text-sm bg-[#FFE600] text-[#0a0a0a] inline-block px-3 py-1 mb-4">
            WHAT WINNING CONTENT LOOKS LIKE
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95]">
            The reels that{" "}
            <span className="bg-[#FFE600] text-[#0a0a0a] px-2">already convert.</span>
          </h2>
          <p className="font-mono-body mt-5 text-[#FDFBF7]/75">
            The faceless and face-on formats converting right now — model your posts on these. Full breakdowns in the Playbook.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
          <Reel
            src={REELS.painHook}
            tag="PAIN HOOK"
            chip="👉 link in bio"
            cap="Lead with the struggle"
            testid="reel-pain"
          />
          <Reel
            src={REELS.trendJack}
            tag="TREND-JACK"
            chip="👉 link in bio"
            cap="Ride a trend → your link"
            testid="reel-trend"
          />
          <Reel
            src={REELS.contrarian}
            tag="CONTRARIAN"
            chip="👉 link in bio"
            cap="Fewer applies → MORE interviews"
            testid="reel-contrarian"
          />
          <Reel
            src={REELS.aiPain}
            tag="AI CREATOR"
            chip="👉 Comment JOB"
            cap="Faceless / AI-influencer angle"
            testid="reel-aipain"
            accent="#FF00FF"
          />
          <Reel
            img={AI_STILLS.c2}
            tag="AI CREATOR"
            chip="👉 Comment JOB"
            cap="\u201CI asked AI to…\u201D skill post"
            testid="reel-c2"
            accent="#FF00FF"
          />
          <Reel
            img={AI_STILLS.c3}
            tag="AI CREATOR"
            chip="👉 Comment JOB"
            cap="Build-your-own AI influencer"
            testid="reel-c3"
            accent="#FF00FF"
          />
        </div>
      </div>
    </section>
  );
}

/* ───────── What to post — swipe file ───────── */
function SwipeFile() {
  const posts = [
    {
      h: "TREND-JACK HOOK",
      lines: [
        "Elon's a \"trillionaire\" at 54. Wild.",
        "",
        "The people quietly winning in 2026 stacked one AI skill into $2–5K/mo — $0 setup.",
        "",
        "Hundreds of documented playbooks, one directory.",
      ],
      cta: "link in bio / comment \"MONEY\"",
      bg: "bg-[#FFE600] text-[#0a0a0a]",
    },
    {
      h: "PAIN HOOK",
      lines: [
        "Applied to 200 jobs. 0 interviews.",
        "",
        "It's not you — it's the ATS black hole. There's a system for getting seen.",
      ],
      cta: "comment \"JOB\" → your link",
      bg: "bg-[#FF3B30] text-white",
    },
    {
      h: "CONTRARIAN HOOK",
      lines: [
        "Applying to MORE jobs is why you're getting fewer interviews.",
        "",
        "Here's the move nobody tells you.",
      ],
      cta: "link in bio",
      bg: "bg-[#0055FF] text-white",
    },
  ];
  return (
    <section
      data-testid="affiliate-swipe"
      className="border-b-4 border-[#0a0a0a] bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="mb-12 reveal max-w-3xl">
          <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-4">
            SWIPE FILE
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95]">
            What to{" "}
            <span className="bg-[#FFE600] px-2">post.</span>
          </h2>
          <p className="font-mono-body mt-5 text-[#0a0a0a]/75">
            Real, ready-to-adapt examples. Swap in your affiliate link / comment keyword.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <article
              key={p.h}
              data-testid={`swipe-${i}`}
              className={`${p.bg} border-4 border-[#0a0a0a] shadow-brutal hover-pop p-6 reveal flex flex-col`}
            >
              <div className="font-accent text-xs bg-[#0a0a0a] text-[#FFE600] inline-block px-2.5 py-1 self-start mb-4">
                {p.h}
              </div>
              <Quote size={26} strokeWidth={2.5} className="opacity-30 mb-3" />
              <div className="font-mono-body text-[13.5px] md:text-sm whitespace-pre-line leading-relaxed flex-1">
                {p.lines.join("\n")}
              </div>
              <div className="mt-5 pt-4 border-t-2 border-dashed border-current/40 font-accent text-[11px] md:text-xs">
                CTA: {p.cta}
              </div>
            </article>
          ))}
        </div>
        <p className="font-mono-body text-sm mt-7 text-[#0a0a0a]/75 max-w-3xl">
          Creator video examples &amp; a swipe file of winning posts live inside the Framework — and we add new ones as affiliates ship them.
        </p>
      </div>
    </section>
  );
}

/* ───────── AI Reddit method ───────── */
function AiReddit() {
  const steps = [
    {
      t: "Find the pain.",
      b: "Point an AI/agent at forums (Reddit subs like r/jobs, r/resumes, r/sidehustle, r/artificial), X and Threads search for posts about job-search struggles, \"how do I make money with AI,\" resume help, layoffs. Pull the ones with real emotion + recent activity.",
    },
    {
      t: "Let AI draft the help.",
      b: "Feed the post to AI and ask for a genuinely useful, human reply that answers their question first — then, only if it fits, mentions the JobHacki playbook/tool that helps, with your link. Lead with value, not a pitch.",
    },
    {
      t: "Reply like a person.",
      b: "No copy-paste spam. Match the thread's tone, add a real tip, disclose it's an affiliate link where required. Helpful > salesy wins every time (and keeps you un-banned).",
    },
    {
      t: "Scale what works.",
      b: "Track which subs/angles convert, make more of those, and turn the best replies into standalone posts and a faceless page. Repeat daily.",
    },
  ];
  return (
    <section
      data-testid="affiliate-reddit"
      className="border-b-4 border-[#0a0a0a] bg-[#FF00FF] text-white"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="mb-12 reveal max-w-3xl">
          <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-4">
            AI-REDDIT METHOD
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95]">
            Or build your own{" "}
            <span className="bg-[#FFE600] text-[#0a0a0a] px-2">AI influencer.</span>
          </h2>
          <p className="font-mono-body mt-5 text-white/85">
            No audience yet? Use AI to find people <i>already asking</i> for what JobHacki solves — and help them (with your link). Done right and in good faith, this is the fastest cold start.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {steps.map((s, i) => (
            <div
              key={s.t}
              data-testid={`reddit-step-${i}`}
              className="bg-white text-[#0a0a0a] border-4 border-[#0a0a0a] shadow-brutal p-6 reveal hover-pop flex flex-col"
            >
              <div className="font-display text-6xl leading-none mb-4">0{i + 1}</div>
              <h3 className="font-display text-xl md:text-2xl mb-3">{s.t}</h3>
              <p className="font-mono-body text-sm leading-relaxed opacity-85 flex-1">
                {s.b}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-8 bg-[#0a0a0a] text-[#FDFBF7] border-4 border-[#FFE600] shadow-[6px_6px_0_0_#FFE600] p-5 md:p-6 reveal flex gap-4 items-start">
          <ShieldCheck size={28} strokeWidth={2.5} className="shrink-0 mt-1 text-[#FFE600]" />
          <p className="font-mono-body text-sm md:text-base">
            <b>Play it straight.</b> Be genuinely helpful, follow each platform&apos;s self-promo rules, and disclose affiliate links. Spam gets you banned; real help compounds.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ───────── Final CTA ───────── */
function FinalCTA() {
  return (
    <section
      data-testid="affiliate-final"
      className="relative border-b-4 border-[#0a0a0a] bg-[#FFE600] overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28 text-center">
        <div className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] px-3 py-1.5 font-accent text-xs md:text-sm shadow-brutal-sm mb-6">
          THE AFFILIATE MARKETING FRAMEWORK
        </div>
        <h2 className="font-display text-[44px] sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.92] max-w-4xl mx-auto">
          Everything above,{" "}
          <span className="bg-[#0a0a0a] text-[#FFE600] px-3">step by step.</span>
        </h2>
        <p className="font-mono-body text-base md:text-lg mt-6 max-w-2xl mx-auto text-[#0a0a0a]/80">
          Influencer, AI-influencer, UGC, blogs, ads, the AI-Reddit method, our hook/thumbnail/caption systems, swipe files and real post examples — the full playbook for promoting JobHacki and winning.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href={PLAYBOOK_PDF}
            download
            data-testid="affiliate-final-pdf"
            className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FF3B30] text-white border-4 border-[#0a0a0a] px-7 py-5 font-accent text-lg md:text-xl shadow-brutal"
          >
            <Download size={18} strokeWidth={3} /> Download the Affiliate Playbook (free)
          </a>
          <a
            href={AFFILIATE_SIGNUP}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="affiliate-final-cta"
            className="hover-pop inline-flex items-center justify-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-4 border-[#0a0a0a] px-7 py-5 font-accent text-lg md:text-xl shadow-brutal"
          >
            Become a JobHacki affiliate <ExternalLink size={16} strokeWidth={3} />
          </a>
        </div>
        <p className="font-mono-body text-xs md:text-sm text-[#0a0a0a]/60 mt-10 max-w-3xl mx-auto leading-relaxed">
          Affiliate earnings depend on your effort and audience — there are no guaranteed results. Income examples reflect what&apos;s possible, not typical. Promote honestly: make accurate claims, disclose affiliate links, and follow each platform&apos;s rules. Commissions are tracked and paid by Whop under its affiliate terms.
        </p>
      </div>
    </section>
  );
}

export default function Affiliate() {
  const { price } = usePrice();
  return (
    <div data-testid="affiliate-page" className="bg-[#FDFBF7] text-[#0a0a0a] min-h-screen">
      <RevealRoot>
        <SEO
          title={`JobHacki Affiliates — Promote a ${price} product, get paid by Whop`}
          description={`Become a JobHacki affiliate: promote a ${price} one-time membership (hundreds of AI, income & job playbooks + tools). Every sale tracked and paid by Whop — a $1.6B platform. Influencer, UGC, AI-influencer, blog and ad playbooks inside.`}
          canonical="https://jobhacki.com/affiliate"
        />
        <Navbar />
        <Hero />
        <WhyConverts />
        <TwoWays />
        <Framework />
        <StackOrProduct />
        <Showcase />
        <SwipeFile />
        <AiReddit />
        <FinalCTA />
        <Footer />
      </RevealRoot>
    </div>
  );
}
